import { useState, useEffect, useRef } from 'react';
import { Play, ArrowLeft, Plus, X, Search, Grid, Square, Video, RefreshCw } from 'lucide-react';
import { showError, showSuccess } from "../../utils/toast";
import baseapi from '../../services/baseapi';
import { useClientLevel } from "../../hooks/useClientLevel";
import { jwt_decode } from '../../utils/jwt_decode';
import { useNavigate } from 'react-router-dom';
interface CameraType {
    id: string;
    name: string;
    status: string;
    url: string;
    client_id?: string;
    agent_id?: string;
}

const VideoPlayer = ({ camera, onStateChange }: { camera: CameraType; onStateChange?: (state: 'connecting' | 'live' | 'offline') => void }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        let pc: RTCPeerConnection | null = null;
        let hls: any = null;
        let retryTimer: any = null;
        let retries = 0;
        const MAX_RETRIES = 6;

        let streamId = '';
        let readToken = '';
        let hlsUrl = '';
        let isUsingHlsFallback = false;
        let isMounted = true;

        const startHls = (url: string, token: string) => {
            if (!isMounted) return;
            const Hls = (window as any).Hls;
            if (!Hls) {
                setError("HLS library not loaded");
                onStateChange?.('offline');
                return;
            }

            if (hls) {
                hls.destroy();
            }

            hls = new Hls({
                debug: false,
                manifestLoadingTimeOut: 65000,
                manifestLoadingMaxRetry: 0,
                xhrSetup: function (xhr: any, url: string) {
                    if (token && !url.includes('token=')) {
                        xhr.open('GET', url + (url.includes('?') ? '&' : '?') + 'token=' + token, true);
                    }
                },
            });

            hls.on(Hls.Events.ERROR, (_: any, d: any) => {
                if (!isMounted) return;
                console.warn(`[hls] ${d.type}/${d.details} fatal=${d.fatal}`);
                if (d.fatal) {
                    hls.destroy();
                    hls = null;
                    // HLS retry logic
                    if (retries < 8) {
                        retries++;
                        const doRefresh = (retries % 2 === 0);
                        console.log(`[hls] Stream not ready - retrying in 3s... (${retries}/8)`);
                        onStateChange?.('connecting');

                        if (doRefresh) {
                            refreshStream().then((freshData) => {
                                if (!isMounted) return;
                                if (freshData) {
                                    hlsUrl = freshData.hls_url;
                                    readToken = freshData.read_token || freshData.hls_url.split('token=')[1] || '';
                                }
                                retryTimer = setTimeout(() => {
                                    if (isMounted) startHls(hlsUrl, readToken);
                                }, 3000);
                            });
                        } else {
                            retryTimer = setTimeout(() => {
                                if (isMounted) startHls(hlsUrl, readToken);
                            }, 3000);
                        }
                    } else {
                        setError("Camera fallback stream unavailable");
                        onStateChange?.('offline');
                    }
                }
            });

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                if (!isMounted) return;
                setError(null);
                onStateChange?.('live');
                video.play().catch(e => console.warn('autoplay: ' + e));
            });

            hls.loadSource(url);
            hls.attachMedia(video);
        };

        const refreshStream = async () => {
            if (!isMounted) return null;
            try {
                const response = await baseapi.post('camera/stream-access', {
                    asset_id: camera.id,
                    client_id: camera.client_id,
                    agent_id: camera.agent_id,
                });
                if (isMounted && response.data.status) {
                    return response.data.data;
                }
            } catch (err) {
                console.error("Failed to refresh stream", err);
            }
            return null;
        };

        const startHlsFallback = () => {
            if (!isMounted) return;
            console.log("[webrtc] Falling back to HLS stream...");
            onStateChange?.('connecting');
            isUsingHlsFallback = true;
            if (pc) {
                pc.close();
                pc = null;
            }
            if (video && video.srcObject) {
                video.srcObject = null;
            }
            if (hlsUrl) {
                const token = readToken || hlsUrl.split('token=')[1] || '';
                startHls(hlsUrl, token);
            } else {
                setError("Camera stream unavailable");
                onStateChange?.('offline');
            }
        };

        const handleRetry = () => {
            if (!isMounted || isUsingHlsFallback) return;

            if (retries >= MAX_RETRIES) {
                startHlsFallback();
                return;
            }

            retries++;
            console.log(`[webrtc] Connection failed/disconnected. Retry ${retries}/${MAX_RETRIES} in 4s...`);
            onStateChange?.('connecting');

            if (retryTimer) clearTimeout(retryTimer);
            retryTimer = setTimeout(async () => {
                if (!isMounted) return;
                if (retries % 2 === 0) {
                    const freshData = await refreshStream();
                    if (!isMounted) return;
                    if (freshData) {
                        streamId = freshData.stream_id;
                        readToken = freshData.read_token;
                        hlsUrl = freshData.hls_url;
                    }
                }
                connectWebRTC();
            }, 4000);
        };

        const connectWebRTC = async () => {
            if (!isMounted || isUsingHlsFallback) return;

            if (pc) {
                pc.close();
                pc = null;
            }

            video.srcObject = null;

            if (!streamId) {
                startHlsFallback();
                return;
            }

            try {
                pc = new RTCPeerConnection({
                    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
                });

                pc.addTransceiver('video', { direction: 'recvonly' });
                pc.addTransceiver('audio', { direction: 'recvonly' });

                pc.ontrack = (event) => {
                    if (!isMounted) return;
                    if (!video.srcObject) {
                        video.srcObject = new MediaStream();
                    }
                    (video.srcObject as MediaStream).addTrack(event.track);
                    setError(null);
                };

                pc.oniceconnectionstatechange = () => {
                    if (!isMounted) return;
                    const state = pc?.iceConnectionState;
                    console.log(`[webrtc] ICE Connection State: ${state}`);
                    if (state === 'connected' || state === 'completed') {
                        retries = 0;
                        setError(null);
                        onStateChange?.('live');
                    } else if (state === 'failed' || state === 'disconnected') {
                        handleRetry();
                    }
                };

                const offer = await pc.createOffer();
                if (!isMounted) return;
                await pc.setLocalDescription(offer);

                // Wait for ICE gathering
                await new Promise<void>((resolve) => {
                    if (!isMounted || pc?.iceGatheringState === 'complete') {
                        resolve();
                        return;
                    }
                    const checkGathering = () => {
                        if (!isMounted || pc?.iceGatheringState === 'complete') {
                            pc?.removeEventListener('icegatheringstatechange', checkGathering);
                            resolve();
                        }
                    };
                    pc?.addEventListener('icegatheringstatechange', checkGathering);
                    setTimeout(() => {
                        pc?.removeEventListener('icegatheringstatechange', checkGathering);
                        resolve();
                    }, 1000);
                });

                if (!isMounted) return;

                const whepUrl = `https://${window.location.hostname}/stream/${streamId}/whep?token=${readToken}`;
                console.log(`[webrtc] WHEP handshake with: ${whepUrl}`);

                const response = await fetch(whepUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/sdp' },
                    body: pc.localDescription?.sdp
                });

                if (!isMounted) return;

                if (!response.ok) {
                    throw new Error(`WHEP failed with status: ${response.status}`);
                }

                const answerSdp = await response.text();
                if (!isMounted) return;

                await pc.setRemoteDescription(new RTCSessionDescription({
                    type: 'answer',
                    sdp: answerSdp
                }));

                console.log("[webrtc] WebRTC playback started successfully");
            } catch (err) {
                console.warn("[webrtc] Handshake failed, scheduling retry:", err);
                handleRetry();
            }
        };

        const initStream = async () => {
            onStateChange?.('connecting');
            const data = await refreshStream();
            if (!isMounted) return;
            if (data) {
                streamId = data.stream_id;
                readToken = data.read_token;
                hlsUrl = data.hls_url;

                if (streamId) {
                    connectWebRTC();
                } else {
                    startHlsFallback();
                }
            } else {
                setError("Failed to get stream access");
                onStateChange?.('offline');
            }
        };

        initStream();

        return () => {
            isMounted = false;
            if (retryTimer) clearTimeout(retryTimer);
            if (pc) {
                pc.close();
                pc = null;
            }
            if (hls) {
                hls.destroy();
                hls = null;
            }
        };
    }, [camera]);

    if (error) {
        return (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black text-white p-4">
                <p className="text-red-500 text-sm mb-2">{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="px-3 py-1 bg-gray-800 rounded text-xs hover:bg-gray-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-black overflow-hidden">
            <video
                ref={videoRef}
                className="w-full h-full"
                controls
                autoPlay
                muted
            />
        </div>
    );
};

export default function Cameras() {
    const [cameras, setCameras] = useState<CameraType[]>([]);
    const [selectedCamera, setSelectedCamera] = useState<CameraType | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newStream, setNewStream] = useState({ name: '', url: '', type: 'on_demand' });
    const [searchQuery, setSearchQuery] = useState('');
    const [isMultiviewOpen, setIsMultiviewOpen] = useState(false);
    const [dropdownAlign, setDropdownAlign] = useState<'right' | 'left'>('right');
    const [multiviewLayout, setMultiviewLayout] = useState(1); // 1, 4, 9, 16, 25
    const [selectedMultiviewCameras, setSelectedMultiviewCameras] = useState<(string | null)[]>([]);
    const [cameraThumbnails, setCameraThumbnails] = useState<Record<string, string>>({});
    const [streamStates, setStreamStates] = useState<Record<string, 'connecting' | 'live' | 'offline'>>({});
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const multiviewDropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const currentLevel = useClientLevel();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isMultiviewOpen && multiviewDropdownRef.current) {
            const rect = multiviewDropdownRef.current.getBoundingClientRect();
            // If button is close to left edge (< 170px), align left to prevent overflowing off-screen
            if (rect.left < 170) {
                setDropdownAlign('left');
            } else {
                setDropdownAlign('right');
            }
        }
    }, [isMultiviewOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (multiviewDropdownRef.current && !multiviewDropdownRef.current.contains(event.target as Node)) {
                setIsMultiviewOpen(false);
            }
        };

        if (isMultiviewOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMultiviewOpen]);

    useEffect(() => {
        const storedThumbnails = localStorage.getItem('cameraThumbnails');
        if (storedThumbnails) {
            setCameraThumbnails(JSON.parse(storedThumbnails));
        }
    }, []);

    useEffect(() => {
        if (Object.keys(cameraThumbnails).length === 0) return;

        let currentThumbnails = { ...cameraThumbnails };
        let success = false;

        while (!success && Object.keys(currentThumbnails).length > 0) {
            try {
                localStorage.setItem('cameraThumbnails', JSON.stringify(currentThumbnails));
                success = true;
            } catch (error) {
                console.warn('LocalStorage quota exceeded. Trimming oldest thumbnail...');
                const keys = Object.keys(currentThumbnails);
                if (keys.length > 0) {
                    delete currentThumbnails[keys[0]];
                }
            }
        }

        // If we had to trim items to fit the quota, sync the trimmed object back to React state
        if (Object.keys(currentThumbnails).length !== Object.keys(cameraThumbnails).length) {
            setCameraThumbnails(currentThumbnails);
        }
    }, [cameraThumbnails]);

    useEffect(() => {
        if (currentLevel.id) {
            setSelectedCamera(null);
            setMultiviewLayout(1);
            setSelectedMultiviewCameras([]);
            setStreamStates({});
            fetchCameras();
        }
        else {
            navigate('/dashboard');
        }
    }, [currentLevel.id]);

    const fetchCameras = async () => {
        setLoading(true);
        try {
            const queryObj: any = { asset_type: 'camera' };
            if (currentLevel.id && currentLevel.name !== 'global') {
                queryObj.client_id = currentLevel.id;
            }

            const response = await baseapi.get('assets', {
                params: {
                    query: JSON.stringify(queryObj),
                    limit: 1000
                }
            });

            if (response.data.status) {
                const jwt = jwt_decode();
                const tenant_name = jwt.tenant_id || jwt.tenant_name;
                const org_id = jwt.organization_id;

                const mappedCameras = response.data.data.map((cam: any) => {
                    const streamPath = tenant_name
                        ? `${tenant_name}-${cam.organization_id || org_id}-${cam.client_id}-${cam.agent_id}_${cam.id}`
                        : `${cam.organization_id || org_id}-${cam.client_id}-${cam.agent_id}_${cam.id}`;

                    return {
                        id: String(cam.id),
                        name: cam.name,
                        status: cam.status === 'active' || cam.status === 'online' ? 'online' : 'offline',
                        url: `https://${window.location.hostname}/${streamPath}/`,
                        client_id: String(cam.client_id),
                        agent_id: String(cam.agent_id)
                    };
                });
                setCameras(mappedCameras);
            } else {
                showError("Failed to fetch cameras");
            }
        } catch (error) {
            console.error("Error fetching cameras:", error);
            showError("Error fetching cameras");
        } finally {
            setLoading(false);
        }
    };

    const handleAddStream = () => {
        if (!newStream.name || !newStream.url) {
            showError("Please fill all fields");
            return;
        }

        const newCam = {
            id: `cam_0${cameras.length + 1}`,
            name: newStream.name,
            status: 'online',
            url: newStream.url
        };

        setCameras([...cameras, newCam]);
        setIsModalOpen(false);
        setNewStream({ name: '', url: '', type: 'on_demand' });
        showSuccess("Stream added successfully (Local)");
    };

    const toggleCameraSelection = (camId: string) => {
        if (selectedMultiviewCameras.includes(camId)) {
            setSelectedMultiviewCameras(prev => prev.map(id => id === camId ? null : id));
        } else {
            const emptyIndex = selectedMultiviewCameras.findIndex(id => id === null);
            if (emptyIndex !== -1) {
                setSelectedMultiviewCameras(prev => {
                    const next = [...prev];
                    next[emptyIndex] = camId;
                    return next;
                });
            } else {
                showError(`Multiview is full for ${Math.sqrt(multiviewLayout)}x${Math.sqrt(multiviewLayout)} layout. Remove a camera first.`);
            }
        }
    };

    const enterMultiview = (layout: number) => {
        setMultiviewLayout(layout);
        setIsMultiviewOpen(false);
        setSelectedCamera(null);
        setSelectedMultiviewCameras(Array(layout).fill(null));
    };

    return (
        <div className="bg-gray-50 dark:bg-gray-900 min-h-screen text-gray-900 dark:text-gray-100">
            {selectedCamera || multiviewLayout > 1 ? (
                <div className="relative flex flex-row h-screen pb-15 bg-white/50 dark:bg-gray-900/80 overflow-hidden">
                    {/* Mobile Backdrop */}
                    {isMobileSidebarOpen && (
                        <div
                            className="fixed inset-0 bg-black/60 backdrop-blur-xs z-40 md:hidden"
                            onClick={() => setIsMobileSidebarOpen(false)}
                        />
                    )}

                    {/* Camera Sidebar (Slide-over drawer on mobile, static on desktop) */}
                    <div className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] md:static md:w-64 md:z-auto transition-transform duration-300 ease-in-out ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'} border-r border-gray-200 dark:border-gray-800 p-4 flex flex-col bg-white dark:bg-gray-900 shadow-2xl md:shadow-none md:bg-white md:dark:bg-gray-900/30`}>
                        <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-bold text-gray-700 dark:text-gray-400 uppercase tracking-wider">All Cameras</h3>
                                {multiviewLayout > 1 && (
                                    <span className="text-xs text-orange-600 dark:text-orange-500 font-medium">
                                        {selectedMultiviewCameras.filter(Boolean).length}/{multiviewLayout}
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={() => setIsMobileSidebarOpen(false)}
                                className="p-1 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white md:hidden cursor-pointer"
                                aria-label="Close sidebar"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="relative mb-4">
                            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
                            <input
                                type="text"
                                placeholder="Search cameras..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-colors"
                            />
                        </div>

                        <div className="space-y-1.5 overflow-y-auto custom-scroll flex-1 pr-1">
                            {cameras.filter(cam => cam.name.toLowerCase().includes(searchQuery.toLowerCase())).map((cam) => (
                                <div
                                    key={cam.id}
                                    onClick={() => {
                                        if (multiviewLayout > 1) {
                                            toggleCameraSelection(cam.id);
                                        } else {
                                            setSelectedCamera(cam);
                                            setMultiviewLayout(1);
                                            setIsMobileSidebarOpen(false);
                                        }
                                    }}
                                    draggable={multiviewLayout > 1 && cam.status === 'online'}
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('cameraId', cam.id);
                                    }}
                                    className={`p-2.5 rounded-lg cursor-pointer transition-colors flex items-center justify-between ${multiviewLayout > 1
                                        ? selectedMultiviewCameras.includes(cam.id)
                                            ? 'bg-orange-600 text-white'
                                            : 'bg-gray-100 dark:bg-gray-800/50 hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                                        : (selectedCamera && selectedCamera.id === cam.id)
                                            ? 'bg-orange-600 text-white'
                                            : 'bg-gray-100 dark:bg-gray-800/50 hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                                        }`}
                                >
                                    <div className="flex items-center gap-2 truncate">
                                        <Video className="w-3.5 h-3.5 shrink-0" />
                                        <span className="text-xs font-medium truncate">{cam.name}</span>
                                    </div>
                                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cam.status === 'online' ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-900 min-w-0">
                        <div className="flex items-center justify-between bg-white dark:bg-gray-900/50 px-3 py-2 border-b border-gray-200 dark:border-gray-800 min-h-[56px] md:min-h-[60px] gap-2">
                            {/* Mobile Drawer Button */}
                            <button
                                onClick={() => setIsMobileSidebarOpen(true)}
                                className="md:hidden flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 text-xs font-semibold cursor-pointer shrink-0"
                                title="Open cameras list"
                            >
                                <Video className="w-3.5 h-3.5 text-orange-600 dark:text-orange-500 shrink-0" />
                                <span>Cameras</span>
                                {multiviewLayout > 1 && (
                                    <span className="px-1.5 py-0.2 rounded-full bg-orange-600 text-[10px] text-white font-bold">
                                        {selectedMultiviewCameras.filter(Boolean).length}
                                    </span>
                                )}
                            </button>

                            {/* Title Section */}
                            <div className="flex items-center min-w-0 flex-1">
                                <h1 className="text-sm sm:text-base md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-1.5 md:gap-2 truncate leading-none">
                                    <Video className="text-orange-600 dark:text-orange-500 w-4 h-4 md:w-5 md:h-5 flex-shrink-0 hidden md:block" />
                                    <span className="truncate">
                                        {multiviewLayout > 1
                                            ? `Multiview ${Math.sqrt(multiviewLayout)}x${Math.sqrt(multiviewLayout)}`
                                            : (selectedCamera ? selectedCamera.name : 'Camera Monitoring')}
                                    </span>
                                </h1>
                            </div>

                            {/* Back Button */}
                            <button
                                onClick={() => {
                                    setSelectedCamera(null);
                                    setMultiviewLayout(1);
                                    setSelectedMultiviewCameras([]);
                                    setStreamStates({});
                                    setIsMobileSidebarOpen(false);
                                }}
                                className="flex items-center gap-1.5 flex-shrink-0 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-colors text-xs sm:text-sm font-semibold border border-gray-200 dark:border-gray-700 whitespace-nowrap cursor-pointer shadow-xs"
                            >
                                <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                                <span className="hidden sm:inline">Back to Cameras</span>
                                <span className="sm:hidden">Back</span>
                            </button>
                        </div>

                        <div className="p-2 pb-15 flex-1 flex flex-col min-h-0 overflow-y-auto md:overflow-hidden">
                            {multiviewLayout === 16 && !isMobile ? (
                                <div className="grid grid-cols-2 grid-rows-2 gap-2 flex-1 min-h-0">
                                    {[0, 1, 2, 3].map(groupIndex => (
                                        <div key={groupIndex} className="bg-white dark:bg-gray-900 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 flex flex-col shadow-sm dark:shadow-2xl p-1 h-full w-full min-h-0">
                                            <div className="grid grid-cols-2 grid-rows-2 gap-1 flex-1 h-full min-h-0">
                                                {[0, 1, 2, 3].map(subIndex => {
                                                    const actualIndex = groupIndex * 4 + subIndex;
                                                    const camId = selectedMultiviewCameras[actualIndex];
                                                    const cam = cameras.find(c => c.id === camId);

                                                    return (
                                                        <div
                                                            key={subIndex}
                                                            className="relative bg-black rounded-lg overflow-hidden border border-gray-800 flex flex-col items-center justify-center group h-full w-full min-h-0"
                                                            onDragOver={(e) => e.preventDefault()}
                                                            onDrop={(e) => {
                                                                e.preventDefault();
                                                                const droppedCamId = e.dataTransfer.getData('cameraId');
                                                                if (droppedCamId) {
                                                                    setSelectedMultiviewCameras(prev => {
                                                                        const next = [...prev];
                                                                        next[actualIndex] = droppedCamId;
                                                                        return next;
                                                                    });
                                                                }
                                                            }}
                                                        >
                                                            {cam ? (
                                                                <>
                                                                    {cam.status === 'online' ? (
                                                                        <VideoPlayer
                                                                            camera={cam}
                                                                            onStateChange={(state) => setStreamStates(prev => ({ ...prev, [cam.id]: state }))}
                                                                        />
                                                                    ) : (
                                                                        <div className="text-center">
                                                                            <Video className="w-6 h-6 text-red-500/50 mx-auto mb-1" />
                                                                            <p className="text-gray-400 text-[10px]">Offline</p>
                                                                        </div>
                                                                    )}
                                                                    <div className="absolute top-2 left-2 right-2 flex justify-between items-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                                        <div className="bg-black/70 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] font-bold text-white border border-white/10 flex items-center gap-1.5 truncate max-w-[70%]">
                                                                            {(() => {
                                                                                const currentStatus = streamStates[cam.id] || 'connecting';
                                                                                const dotColor = currentStatus === 'live' ? 'bg-emerald-500' : currentStatus === 'connecting' ? 'bg-amber-500' : 'bg-red-500';
                                                                                return <span className={`w-1.5 h-1.5 rounded-full ${dotColor} shrink-0`}></span>;
                                                                            })()}
                                                                            <span className="truncate">{cam.name}</span>
                                                                        </div>
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setSelectedMultiviewCameras(prev => {
                                                                                    const next = [...prev];
                                                                                    next[actualIndex] = null;
                                                                                    return next;
                                                                                });
                                                                            }}
                                                                            className="bg-black/70 backdrop-blur-md p-1 rounded-full text-gray-400 hover:text-red-500 border border-white/10 transition-colors cursor-pointer"
                                                                        >
                                                                            <X className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <div className="flex flex-col items-center justify-center">
                                                                    <Plus className="w-5 h-5 text-gray-400 dark:text-gray-700 mb-1" />
                                                                    <p className="text-gray-500 dark:text-gray-600 text-[10px]">Drop here</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : multiviewLayout > 1 ? (
                                <div
                                    className={isMobile ? "grid grid-cols-1 sm:grid-cols-2 gap-3 pb-8" : "grid flex-1 min-h-0 w-full"}
                                    style={isMobile ? undefined : {
                                        gridTemplateColumns: `repeat(${Math.sqrt(multiviewLayout)}, minmax(0, 1fr))`,
                                        gridTemplateRows: `repeat(${Math.sqrt(multiviewLayout)}, minmax(0, 1fr))`,
                                        gap: multiviewLayout === 4 ? '0.75rem' : multiviewLayout === 9 ? '0.5rem' : '0.375rem'
                                    }}
                                >
                                    {Array.from({ length: multiviewLayout }).map((_, index) => {
                                        const camId = selectedMultiviewCameras[index];
                                        const cam = cameras.find(c => c.id === camId);
                                        const isHighDensity = multiviewLayout >= 9;

                                        return (
                                            <div
                                                key={index}
                                                className={`bg-white dark:bg-gray-900 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 flex flex-col shadow-sm dark:shadow-2xl ${isMobile ? 'aspect-video w-full' : 'h-full w-full min-h-0'}`}
                                                onDragOver={(e) => {
                                                    e.preventDefault();
                                                }}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    const droppedCamId = e.dataTransfer.getData('cameraId');
                                                    if (droppedCamId) {
                                                        setSelectedMultiviewCameras(prev => {
                                                            const next = [...prev];
                                                            next[index] = droppedCamId;
                                                            return next;
                                                        });
                                                    }
                                                }}
                                            >
                                                {cam ? (
                                                    <div className="relative flex-1 bg-black flex flex-col items-center justify-center group h-full w-full min-h-0">
                                                        {isHighDensity && !isMobile ? (
                                                            <>
                                                                {/* Compact, elegant glassmorphic status & name badge, only shown on hover */}
                                                                <div className="absolute top-2 left-2 bg-black/75 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] font-bold text-white border border-white/10 flex items-center gap-1.5 truncate max-w-[75%] opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 shadow-lg">
                                                                    {(() => {
                                                                        const currentStatus = streamStates[cam.id] || 'connecting';
                                                                        const dotColor = currentStatus === 'live' ? 'bg-emerald-500' : currentStatus === 'connecting' ? 'bg-amber-500' : 'bg-red-500';
                                                                        return <span className={`w-1.5 h-1.5 rounded-full ${dotColor} shrink-0`}></span>;
                                                                    })()}
                                                                    <span className="truncate">{cam.name}</span>
                                                                </div>
                                                                {/* Close button pill badge, only shown on hover */}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedMultiviewCameras(prev => {
                                                                            const next = [...prev];
                                                                            next[index] = null;
                                                                            return next;
                                                                        });
                                                                    }}
                                                                    className="absolute top-2 right-2 bg-black/75 backdrop-blur-md p-1.5 rounded-full text-gray-400 hover:text-red-500 border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 shadow-lg hover:scale-105 cursor-pointer"
                                                                >
                                                                    <X className="w-3.5 h-3.5" />
                                                                </button>
                                                            </>
                                                        ) : (
                                                            /* Standard Overlay for 2x2 View and Mobile */
                                                            <div className="absolute top-0 left-0 right-0 p-2.5 sm:p-3 bg-gradient-to-b from-black/80 to-transparent flex justify-between items-center z-10">
                                                                <div className="truncate mr-2">
                                                                    <h2 className="text-xs sm:text-sm font-bold text-white truncate">{cam.name}</h2>
                                                                </div>
                                                                <div className="flex items-center gap-2 shrink-0">
                                                                    {(() => {
                                                                        const currentStatus = streamStates[cam.id] || 'connecting';
                                                                        const dotColor = currentStatus === 'live' ? 'bg-emerald-500' : currentStatus === 'connecting' ? 'bg-amber-500' : 'bg-red-500';
                                                                        return (
                                                                            <>
                                                                                <span className={`w-1.5 h-1.5 rounded-full ${dotColor} animate-pulse`}></span>
                                                                                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-300">
                                                                                    {currentStatus === 'live' ? 'Live' : currentStatus === 'connecting' ? 'Connecting...' : 'Offline'}
                                                                                </span>
                                                                            </>
                                                                        );
                                                                    })()}
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedMultiviewCameras(prev => {
                                                                                const next = [...prev];
                                                                                next[index] = null;
                                                                                return next;
                                                                            });
                                                                        }}
                                                                        className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer p-0.5"
                                                                    >
                                                                        <X className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {cam.status === 'online' ? (
                                                            <VideoPlayer
                                                                camera={cam}
                                                                onStateChange={(state) => setStreamStates(prev => ({ ...prev, [cam.id]: state }))}
                                                            />
                                                        ) : (
                                                            <div className="text-center">
                                                                <Video className={`${multiviewLayout === 25 && !isMobile ? 'w-6 h-6' : 'w-8 h-8'} text-red-500/50 mx-auto mb-1.5`} />
                                                                <p className="text-gray-400 text-xs">Offline</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div
                                                        onClick={() => {
                                                            if (isMobile) {
                                                                setIsMobileSidebarOpen(true);
                                                            }
                                                        }}
                                                        className={`relative flex-1 bg-gray-50 dark:bg-gray-900/30 flex flex-col items-center justify-center border border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-2 hover:border-orange-500/50 hover:bg-gray-100/80 dark:hover:bg-gray-800/10 transition-all duration-300 group ${isMobile ? 'cursor-pointer' : ''}`}
                                                    >
                                                        <Plus className={`${multiviewLayout === 25 && !isMobile ? 'w-4 h-4 mb-1' : 'w-6 h-6 mb-2'} text-gray-400 dark:text-gray-700 group-hover:text-orange-500 group-hover:scale-110 transition-all duration-300`} />
                                                        <p className={`${multiviewLayout === 25 && !isMobile ? 'text-[10px]' : 'text-xs'} text-gray-500 dark:text-gray-600 group-hover:text-gray-700 dark:group-hover:text-gray-400 transition-colors text-center px-2`}>
                                                            {isMobile ? 'Tap to select camera' : 'Drop here'}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                selectedCamera && (
                                    <div className="w-full max-w-5xl mx-auto bg-white dark:bg-gray-900 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 flex flex-col shadow-sm dark:shadow-2xl">
                                        <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
                                            <div className="truncate mr-3 sm:mr-4">
                                                <h2 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate">{selectedCamera.name}</h2>
                                            </div>
                                            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                                                {(() => {
                                                    const currentStatus = streamStates[selectedCamera.id] || 'connecting';
                                                    const dotColor = currentStatus === 'live' ? 'bg-emerald-500' : currentStatus === 'connecting' ? 'bg-amber-500' : 'bg-red-500';
                                                    return (
                                                        <>
                                                            <span className={`w-2 h-2 rounded-full ${dotColor} animate-pulse`}></span>
                                                            <span className="text-[11px] sm:text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-300">
                                                                {currentStatus === 'live' ? 'Live' : currentStatus === 'connecting' ? 'Connecting...' : 'Offline'}
                                                            </span>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>

                                        <div className="relative aspect-video bg-black flex flex-col items-center justify-center group w-full">
                                            {selectedCamera.status === 'online' ? (
                                                <VideoPlayer
                                                    camera={selectedCamera}
                                                    onStateChange={(state) => setStreamStates(prev => ({ ...prev, [selectedCamera.id]: state }))}
                                                />
                                            ) : (
                                                <div className="text-center p-4">
                                                    <Video className="w-10 h-10 sm:w-12 sm:h-12 text-red-500/50 mx-auto mb-2 sm:mb-4" />
                                                    <p className="text-gray-400 text-xs sm:text-sm">Camera is offline</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="p-2 mx-auto">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-3">
                        <div className="flex items-center gap-3">
                            <div className='bg-orange-600 p-1.5 rounded-lg border border-orange-500/30 shrink-0'>
                                <Video className='text-white w-4 h-4' />
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">Monitoring System</h1>
                            </div>
                        </div>
                        <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap">
                            <button
                                onClick={fetchCameras}
                                className="h-8 w-8 flex items-center justify-center rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all shrink-0 cursor-pointer shadow-xs"
                                title="Refresh cameras"
                            >
                                <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
                            </button>

                            <div className="relative flex items-center">
                                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
                                <input
                                    type="text"
                                    placeholder="Search cameras..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="h-8 bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 transition-all duration-300 w-32 sm:w-48 sm:focus:w-60 shadow-xs"
                                />
                            </div>

                            <div className="h-8 flex items-center bg-orange-50 dark:bg-orange-600/10 border border-orange-200 dark:border-orange-500/20 px-3 rounded-lg shrink-0">
                                <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">{cameras.filter((c) => c.status === 'online').length} Online</span>
                            </div>

                            <div className="relative shrink-0" ref={multiviewDropdownRef}>
                                <button
                                    onClick={() => setIsMultiviewOpen(!isMultiviewOpen)}
                                    className="h-8 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-white px-3 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 cursor-pointer shadow-xs"
                                >
                                    <Grid className="w-3.5 h-3.5 text-gray-500 dark:text-gray-300" />
                                    <span>Multiview</span>
                                </button>
                                {isMultiviewOpen && (
                                    <div className={`absolute ${dropdownAlign === 'left' ? 'left-0' : 'right-0'} mt-2 w-40 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden max-w-[calc(100vw-24px)]`}>
                                        <div className="py-1">
                                            <button onClick={() => { setMultiviewLayout(1); setIsMultiviewOpen(false); setSelectedCamera(cameras[0]); }} className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white flex items-center gap-2 transition-colors cursor-pointer">
                                                <Square className="w-3.5 h-3.5 text-gray-400" /> 1x1 View
                                            </button>
                                            <button onClick={() => enterMultiview(4)} className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white flex items-center gap-2 transition-colors cursor-pointer">
                                                <Grid className="w-3.5 h-3.5 text-gray-400" /> 2x2 View
                                            </button>
                                            <button onClick={() => enterMultiview(9)} className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white flex items-center gap-2 transition-colors cursor-pointer">
                                                <Grid className="w-3.5 h-3.5 text-gray-400" /> 3x3 View
                                            </button>
                                            <button onClick={() => enterMultiview(16)} className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white flex items-center gap-2 transition-colors cursor-pointer">
                                                <Grid className="w-3.5 h-3.5 text-gray-400" /> 4x4 View
                                            </button>
                                            <button onClick={() => enterMultiview(25)} className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white flex items-center gap-2 transition-colors cursor-pointer">
                                                <Grid className="w-3.5 h-3.5 text-gray-400" /> 5x5 View
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {loading && cameras.length === 0 ? (
                        <div className="flex flex-col items-center justify-center bg-white dark:bg-gray-900/50 border border-dashed border-gray-200 dark:border-gray-800 rounded-2xl p-12 py-24 mt-4 shadow-sm">
                            <RefreshCw className="w-10 h-10 text-orange-500 animate-spin mb-4" />
                            <h3 className="text-gray-800 dark:text-gray-300 font-bold text-base mb-1 font-sans">Loading monitoring system...</h3>
                            <p className="text-gray-500 text-xs font-sans">Connecting to stream registry</p>
                        </div>
                    ) : cameras.filter(cam => cam.name.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 ? (
                        <div className="flex flex-col items-center justify-center bg-white dark:bg-gray-900/50 border border-dashed border-gray-200 dark:border-gray-800 rounded-2xl p-12 py-20 mt-4 shadow-sm">
                            <div className="bg-gray-100 dark:bg-gray-800/50 p-4 rounded-full border border-gray-200 dark:border-gray-700/50 mb-4 shadow-sm">
                                <Video className="w-10 h-10 text-orange-500" />
                            </div>
                            <h3 className="text-gray-900 dark:text-gray-200 font-bold text-base mb-1 font-sans">
                                {searchQuery ? "No matching cameras" : "No cameras found"}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-500 text-sm max-w-xs text-center mb-6 font-sans">
                                {searchQuery
                                    ? "We couldn't find any cameras matching your search term. Try a different search."
                                    : "Connect your IP cameras to start monitoring your physical assets in real-time."
                                }
                            </p>
                            {!searchQuery && (
                                <button
                                    onClick={fetchCameras}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-orange-50 dark:bg-orange-600/10 hover:bg-orange-100 dark:hover:bg-orange-600/20 border border-orange-200 dark:border-orange-500/30 text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 rounded-lg text-xs font-semibold transition-all shadow-sm cursor-pointer"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                                    <span>Refresh Devices</span>
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 pb-20">
                            {cameras.filter(cam => cam.name.toLowerCase().includes(searchQuery.toLowerCase())).map((camera) => (
                                <div
                                    key={camera.id}
                                    className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden hover:border-orange-300 dark:hover:border-gray-700/70 transition-all duration-300 shadow-sm hover:shadow-md dark:hover:shadow-2xl dark:hover:shadow-orange-500/5 group flex flex-col h-full"
                                >
                                    <div className="relative aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center group-hover:bg-gray-200/70 dark:group-hover:bg-gray-800/70 transition-colors">
                                        {cameraThumbnails[camera.id] ? (
                                            <img src={cameraThumbnails[camera.id]} className="w-full h-full object-cover" />
                                        ) : (
                                            <Video className={`w-12 h-12 ${camera.status === 'online' ? 'text-gray-400 dark:text-gray-600 group-hover:text-orange-500/70' : 'text-gray-300 dark:text-gray-700'} transition-colors duration-300`} />
                                        )}

                                        <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/60 backdrop-blur-md px-2 py-1 rounded-full border border-white/10">
                                            <span className={`w-2 h-2 rounded-full ${camera.status === 'online' ? 'bg-emerald-500' : 'bg-red-500'} animate-pulse`}></span>
                                            <span className="text-[10px] font-bold text-white uppercase tracking-wider">{camera.status}</span>
                                        </div>
                                    </div>

                                    <div className="p-4 flex flex-col flex-1 justify-between">
                                        <div className="mb-4">
                                            <h3 className="text-base font-bold text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">{camera.name}</h3>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">RTSP Stream Available</p>
                                        </div>

                                        <button
                                            onClick={() => { setSelectedCamera(camera); setMultiviewLayout(1); }}
                                            disabled={camera.status !== 'online'}
                                            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer
                                                            ${camera.status === 'online'
                                                    ? 'bg-orange-50 dark:bg-amber-700/40 hover:bg-orange-100 dark:hover:bg-orange-600/20 text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 border border-orange-200 dark:border-orange-500/20 hover:border-orange-300 dark:hover:border-orange-500/40 shadow-xs'
                                                    : 'bg-gray-100 dark:bg-gray-800/50 text-gray-400 dark:text-gray-600 cursor-not-allowed border border-gray-200 dark:border-gray-700/50'
                                                }`}
                                        >
                                            <Play className="w-4 h-4" />
                                            <span>Stream</span>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl w-full max-w-md p-6 shadow-2xl">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add New Stream</h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white cursor-pointer">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Stream Name</label>
                                <input
                                    type="text"
                                    value={newStream.name}
                                    onChange={(e) => setNewStream({ ...newStream, name: e.target.value })}
                                    className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
                                    placeholder="e.g. Front Entrance"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Stream URL</label>
                                <input
                                    type="text"
                                    value={newStream.url}
                                    onChange={(e) => setNewStream({ ...newStream, url: e.target.value })}
                                    className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
                                    placeholder="rtsp://..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Stream Type</label>
                                <select
                                    value={newStream.type}
                                    onChange={(e) => setNewStream({ ...newStream, type: e.target.value })}
                                    className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:border-orange-500"
                                >
                                    <option value="on_demand">On Demand</option>
                                    <option value="persistent">Persistent Connection</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-white border border-gray-200 dark:border-transparent rounded-lg text-sm font-semibold transition-colors cursor-pointer"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddStream}
                                className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-sm font-semibold transition-colors cursor-pointer"
                            >
                                Add Stream
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}